import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { MinusIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { classifyTicketSearch, type TicketSearchGroup } from '@erp/shared';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ticketSearchQuery } from './tickets-queries';
import { EmptyData } from './ticket-chips';
import { TicketsTable } from './TicketList';
import { ticketSearchHint, useTenantProfile } from '@/shared/tenant-profile';

export const ticketSearchSearchSchema = z.object({
  q: z.string().default(''),
});
export type TicketSearchSearch = z.infer<typeof ticketSearchSearchSchema>;

/**
 * Busca por nome, identificador ou telefone em TODAS as mesas visíveis,
 * agrupada por mesa (spec 012). O cliente liga e diz uma de três coisas — o
 * nome, o identificador do recurso ou o telefone — e o mesmo carro tem história em mais de uma mesa: a busca da
 * lista, que é sempre dentro de uma mesa, obrigava a repetir em cada uma.
 * Todos os status: o ticket fechado de instalação é parte da história.
 */
export function TicketSearchPage() {
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });
  const search = useMemo(() => ticketSearchSearchSchema.parse(rawSearch), [rawSearch]);
  const q = search.q.trim();

  const [qInput, setQInput] = useState(search.q);
  // O que ESTE campo mandou para a URL. Uma busca nova pelo campo do topo,
  // com esta página aberta, muda a URL por fora e o campo precisa acompanhar;
  // o eco do próprio debounce não — ele chegaria depois de mais teclas e
  // apagaria o que a pessoa acabou de digitar.
  const pushed = useRef(search.q);
  useEffect(() => {
    if (search.q !== pushed.current) {
      pushed.current = search.q;
      setQInput(search.q);
    }
  }, [search.q]);

  // Busca com debounce (300ms) -> parâmetro `q` na URL (padrão de TicketsPage)
  useEffect(() => {
    if (qInput === search.q) return;
    const t = setTimeout(() => {
      pushed.current = qInput;
      void navigate({ to: '/tickets/search', search: { q: qInput }, replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [qInput, search.q, navigate]);

  const terms = classifyTicketSearch(q);
  // spec 017: "placa" é o identificador do recurso no perfil do tenant
  const tenantProfile = useTenantProfile();
  const hint = ticketSearchHint(tenantProfile);
  const idLabel = tenantProfile.terms.resourceId;
  const searchQ = useQuery({
    ...ticketSearchQuery(q),
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="flex min-h-full flex-col gap-3 rounded-lg border border-border/60 bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold leading-none text-foreground">Buscar tickets</h1>
        <div className="relative ml-auto">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={hint.charAt(0).toUpperCase() + hint.slice(1)}
            aria-label={`Buscar tickets por ${hint}`}
            className="h-8 w-72 pl-8 text-[13px]"
          />
        </div>
      </div>

      {q ? (
        <p className="text-xs text-muted-foreground">
          {[
            terms.plate ? `${idLabel} ${terms.plate}` : null,
            terms.platePrefix ? `${idLabel} começando com ${terms.platePrefix}` : null,
            terms.phone ? `telefone terminado em ${terms.phone}` : null,
            `texto “${terms.text}”`,
          ]
            .filter(Boolean)
            .join(' · ')}{' '}
          — em todas as mesas que você vê, abertos e fechados.
        </p>
      ) : null}

      {!q ? (
        <EmptyData message={`Digite o ${hint}`} />
      ) : searchQ.isPending ? (
        <SearchSkeleton />
      ) : searchQ.isError ? (
        <EmptyData message="Erro ao buscar tickets" />
      ) : searchQ.data.groups.length === 0 ? (
        <EmptyData message="Nenhum ticket encontrado" />
      ) : (
        <div className="flex flex-col">
          {searchQ.data.groups.map((g) => (
            <DeskSection key={g.desk.id} group={g} />
          ))}
          {searchQ.data.truncated ? (
            <p className="px-2 py-2 text-xs text-muted-foreground">
              Exibindo os{' '}
              {searchQ.data.groups.reduce((n, g) => n + g.items.length, 0)} mais recentes de{' '}
              {searchQ.data.total} tickets — refine a busca.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** uma mesa com os tickets dela — a busca (012) e os tickets do veículo/associado (032) */
export function DeskSection({ group }: { group: TicketSearchGroup }) {
  const [open, setOpen] = useState(true);
  // a coluna do recurso só existe onde ele é o identificador extraído (placa)
  const showPlate = useTenantProfile().resource.source === 'ticket_identifier';
  const cut = group.items.length < group.total;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-[13px] font-semibold text-foreground transition-colors hover:bg-muted/50">
        {open ? (
          <MinusIcon className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <PlusIcon className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate" title={group.desk.name}>
          Mesa: {group.desk.name}
        </span>
        <span className="font-normal text-muted-foreground">
          - ({cut ? `${group.items.length} de ${group.total}` : group.total})
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {group.items.length > 0 ? (
          <TicketsTable tickets={group.items} showClosedAt showPlate={showPlate} />
        ) : (
          <p className="mb-2 px-2 text-xs text-muted-foreground">
            Os mais recentes de outras mesas ocuparam o limite — refine a busca.
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function SearchSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      {[0, 1].map((g) => (
        <div key={g} className="flex flex-col gap-2">
          <div className="flex items-center gap-2 px-2">
            <Skeleton className="size-3.5 rounded-sm" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="rounded-md border border-border/70">
            {[0, 1, 2].map((r) => (
              <div key={r} className="flex items-center gap-4 border-b px-3 py-2.5 last:border-0">
                <Skeleton className="h-4 w-14" />
                <Skeleton className="h-4 max-w-72 flex-1" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-[22px] w-16 rounded-[4px]" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
